import {
  Bold,
  Heading2,
  ImagePlus,
  Italic,
  Languages,
  Link as LinkIcon,
  List,
  RefreshCw,
} from 'lucide-react';
import { useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { MAX_UPLOAD_MB, mimeOf, uploadOriginal } from '../lib/upload';
import { Card, Field, VisibleToggle, btnGhost, btnSecondary, inputCls } from './ui';

/**
 * Parti della pagina per gli ospiti che si possono nascondere senza cancellarle (API
 * `GUEST_SECTIONS`): nascoste restano nel pannello con tutto quello che c'è dentro.
 */
export const GUEST_SECTIONS = [
  ['banner', 'Immagine in testa', 'la foto bassa sopra il benvenuto'],
  ['buttons', 'Tasti sotto il benvenuto', 'tutti insieme (uno per uno: l’occhio sul tasto)'],
  ['events', 'Eventi', 'eventi vicini e da non perdere'],
  ['itineraries', 'Itinerari', 'in Cosa vedere, se la struttura ne ha'],
  ['places', 'Luoghi da vedere', 'in Cosa vedere'],
  ['beaches', 'Spiagge', 'la scelta guidata della spiaggia'],
  ['activities', 'Attività', 'i mercati settimanali'],
  ['custom', 'Scheda della struttura', 'la scheda qui sotto'],
] as const;
export type GuestSection = (typeof GUEST_SECTIONS)[number][0];
export type GuestSections = Partial<Record<GuestSection, boolean>>;

export interface GuestCustom {
  tab: string;
  title: string;
  body: string;
  first: boolean;
}
export const EMPTY_CUSTOM: GuestCustom = { tab: '', title: '', body: '', first: false };

const LANG_NAMES: Record<string, string> = {
  en: 'inglese',
  de: 'tedesco',
  fr: 'francese',
  es: 'spagnolo',
};
const MAX_BODY = 20000;

/** Sezioni accese e spente della pagina (assenti = accese). */
export function SectionsCard({
  value,
  onChange,
}: {
  value: GuestSections;
  onChange: (v: GuestSections) => void;
}) {
  return (
    <Card title="Cosa si vede nella pagina">
      <p className="text-sm text-muted">
        Togliete la spunta per nascondere una parte: non si cancella niente, si rimette con un clic.
        Cosa vedere sparisce se sono nascosti sia gli itinerari sia i luoghi.
      </p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {GUEST_SECTIONS.map(([key, label, hint]) => (
          <li key={key}>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={value[key] !== false}
                onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
              />
              <span>
                <span className="font-medium">{label}</span>
                <span className="block text-xs text-muted">{hint}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Scheda personalizzata della struttura: nome corto della scheda, titolo e testo in Markdown con
 * immagini (caricate con `/uploads` e ridotte dalla Lambda media in `media/guests/<slug>/`),
 * anteprima; la traduzione automatica la mostra `TranslationStatus`.
 */
export function CustomPageCard({
  slug,
  value,
  onChange,
  visible,
  onVisible,
}: {
  /** struttura già salvata (le immagini si caricano solo allora) */
  slug: string | null;
  value: GuestCustom;
  onChange: (v: GuestCustom) => void;
  /** la scheda si vede sulla pagina (`sections.custom`) */
  visible: boolean;
  onVisible: (v: boolean) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const area = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const set = <K extends keyof GuestCustom>(k: K, v: GuestCustom[K]) =>
    onChange({ ...value, [k]: v });

  /** Inserisce o avvolge il testo selezionato e rimette il cursore. */
  const insert = (before: string, after = '', placeholder = '') => {
    const el = area.current;
    const start = el?.selectionStart ?? value.body.length;
    const end = el?.selectionEnd ?? value.body.length;
    const selected = value.body.slice(start, end) || placeholder;
    const next = value.body.slice(0, start) + before + selected + after + value.body.slice(end);
    set('body', next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };
  /** A inizio riga (titoli, elenchi). */
  const line = (prefix: string) => {
    const el = area.current;
    const start = el?.selectionStart ?? value.body.length;
    const from = value.body.lastIndexOf('\n', start - 1) + 1;
    set('body', value.body.slice(0, from) + prefix + value.body.slice(from));
    requestAnimationFrame(() => el?.focus());
  };

  const upload = async (f: File) => {
    if (!slug) return;
    const type = mimeOf(f);
    if (!type || type === 'application/pdf') {
      setMsg('Immagine non valida: JPEG, PNG, WebP o HEIC.');
      return;
    }
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setMsg(`Immagine oltre ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setMsg(null);
    try {
      setBusy('Caricamento… 0%');
      const key = await uploadOriginal(api, f, type, (pct) => setBusy(`Caricamento… ${pct}%`));
      setBusy('Preparo le misure dell’immagine…');
      const { image } = await api<{ image: { key: string } }>(`/guests/${slug}/image`, {
        method: 'POST',
        body: { uploadKey: key },
      });
      const alt = f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
      insert(`\n\n![${alt}](/media/${image.key}-1200.webp)\n\n`);
      setMsg(
        'Immagine inserita: scrivi tra le parentesi quadre cosa mostra (serve a chi non vede).',
      );
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const tool = `${btnGhost} h-8`;

  return (
    <Card
      title="Scheda della struttura"
      actions={<VisibleToggle visible={visible} onChange={onVisible} />}
    >
      <p className="text-sm text-muted">
        Una scheda in più nella pagina per gli ospiti, scritta da voi in italiano: chi siete, la
        colazione, i servizi, le regole della casa. Al salvataggio si traduce in automatico in
        inglese, tedesco, francese e spagnolo; finché la traduzione non c’è, le altre lingue
        mostrano l’italiano.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
        <Field
          label="Nome della scheda"
          hint="Corto, facoltativo: vuoto è «La struttura» (tradotto). È sempre la prima scheda."
        >
          <input
            className={inputCls}
            value={value.tab}
            maxLength={24}
            onChange={(e) => set('tab', e.target.value)}
          />
        </Field>
        <Field label="Titolo">
          <input
            className={inputCls}
            value={value.title}
            maxLength={120}
            onChange={(e) => set('title', e.target.value)}
          />
        </Field>
      </div>
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Formato">
            <button type="button" className={tool} onClick={() => insert('**', '**', 'grassetto')}>
              <Bold className="size-4" aria-hidden /> <span className="sr-only">Grassetto</span>
            </button>
            <button type="button" className={tool} onClick={() => insert('_', '_', 'corsivo')}>
              <Italic className="size-4" aria-hidden /> <span className="sr-only">Corsivo</span>
            </button>
            <button type="button" className={tool} onClick={() => line('## ')}>
              <Heading2 className="size-4" aria-hidden /> <span className="sr-only">Titoletto</span>
            </button>
            <button type="button" className={tool} onClick={() => line('- ')}>
              <List className="size-4" aria-hidden /> <span className="sr-only">Elenco</span>
            </button>
            <button
              type="button"
              className={tool}
              onClick={() => insert('[', '](https://)', 'testo del link')}
            >
              <LinkIcon className="size-4" aria-hidden /> <span className="sr-only">Link</span>
            </button>
            <button
              type="button"
              className={tool}
              disabled={!slug || !!busy}
              title={slug ? undefined : 'Salva la struttura per caricare le immagini'}
              onClick={() => file.current?.click()}
            >
              <ImagePlus className="size-4" aria-hidden /> Immagine
            </button>
            <input
              ref={file}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void upload(f);
              }}
            />
          </div>
          <div className="flex text-xs">
            <button
              type="button"
              onClick={() => setPreview(false)}
              className={`px-2 py-1 ${!preview ? 'bg-fg text-page' : 'bg-surface'}`}
            >
              Testo
            </button>
            <button
              type="button"
              onClick={() => setPreview(true)}
              className={`px-2 py-1 ${preview ? 'bg-fg text-page' : 'bg-surface'}`}
            >
              Anteprima
            </button>
          </div>
        </div>
        {preview ? (
          <div className="prose-admin min-h-64 border border-fg/25 p-3 text-sm">
            {value.title && <h2>{value.title}</h2>}
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ src, alt }) => (
                  <img
                    src={
                      typeof src === 'string' && src.startsWith('/media/') ? `${media}${src}` : src
                    }
                    alt={alt ?? ''}
                    className="h-auto w-full max-w-xl"
                  />
                ),
              }}
            >
              {value.body || '_Nessun testo_'}
            </Markdown>
          </div>
        ) : (
          <textarea
            ref={area}
            className={`${inputCls} h-80 py-2 font-mono text-[13px]`}
            value={value.body}
            maxLength={MAX_BODY}
            onChange={(e) => set('body', e.target.value)}
          />
        )}
        <span className="mt-1 block text-xs text-muted">
          Markdown: **grassetto**, _corsivo_, ## titoletto, - elenchi, [link](https://…), immagini
          con il pulsante · {value.body.length}/{MAX_BODY}
        </span>
        {(busy || msg) && <p className="mt-2 text-sm">{busy ?? msg}</p>}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.first}
          onChange={(e) => set('first', e.target.checked)}
        />
        Apri la pagina su questa scheda (altrimenti si apre sulla seconda)
      </label>
    </Card>
  );
}

/**
 * Stato della traduzione automatica dei testi per gli ospiti (benvenuto, tasti, scheda della
 * struttura: Lambda enrich, dopo il salvataggio) e "Traduci di nuovo" se manca.
 */
export function TranslationStatus({
  slug,
  i18n,
  saved,
}: {
  slug: string;
  /** traduzioni salvate (`customI18n`) */
  i18n?: Record<string, unknown> & { at?: string };
  /** testi uguali a quelli salvati (lo stato della traduzione vale per loro) */
  saved: boolean;
}) {
  const { api } = useSession();
  const [msg, setMsg] = useState<string | null>(null);
  const retranslate = async () => {
    try {
      await api(`/guests/${slug}/translate`, { method: 'POST' });
      setMsg('Traduzione chiesta: tra un minuto ricarica la pagina per vederne lo stato.');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  };
  const done = Object.keys(LANG_NAMES).filter((l) => i18n?.[l]);
  return (
    <div className="flex flex-wrap items-center gap-3 bg-surface p-3 text-sm">
      <Languages className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        {msg ??
          (!saved
            ? 'Testi per gli ospiti modificati: la traduzione parte quando salvi.'
            : done.length === 4
              ? `Benvenuto, tasti e scheda tradotti in inglese, tedesco, francese e spagnolo${i18n?.at ? ` (${formatStamp(i18n.at)})` : ''}.`
              : done.length > 0
                ? `Tradotti solo in ${done.map((l) => LANG_NAMES[l]).join(', ')}.`
                : 'Traduzione in corso o non riuscita (tetto giornaliero dell’AI): nelle altre lingue si vede l’italiano.')}
      </span>
      {saved && done.length < 4 && !msg && (
        <button type="button" className={btnSecondary} onClick={retranslate}>
          <RefreshCw className="size-4" aria-hidden /> Traduci di nuovo
        </button>
      )}
    </div>
  );
}
